import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { vLabel } from "@/components/BulkQuoteDialog";
import { Loader2, ShoppingCart, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const money = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => Number(String(v).replace(",", "."));
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
const sizeKey = (l) => { const n = parseFloat(String(l).replace(/^No:\s*/, "").replace(",", ".")); return Number.isFinite(n) ? n : Infinity; };
const TH = "text-[10px] uppercase tracking-widest text-slate-500 font-mono px-2 py-1.5";

// Toplu Satın Al: aynı ürünün tüm beden/numara teklifleri → tedarikçi başına TEK PO (mevcut POST /purchase-quotes/create-pos, multi_variant)
export default function BulkPurchaseDialog({ request, onClose, onSaved }) {
  const mats = useMemo(() => {
    const m = {};
    (request?.items || []).forEach((it) => { (m[`${it.kind}::${it.material_id}`] ||= { kind: it.kind, material_id: it.material_id, name: it.name, unit: it.unit || "adet", items: [] }).items.push(it); });
    return Object.values(m);
  }, [request]);
  const [mk, setMk] = useState("");
  const mat = mats.find((m) => `${m.kind}::${m.material_id}` === mk) || mats[0];
  const [data, setData] = useState(null);
  const [pick, setPick] = useState({});
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(uid);

  const load = async () => {
    if (!mat) return;
    const { data: d } = await api.get("/purchase-quotes", { params: { material_id: mat.material_id, kind: mat.kind, all_variants: true, request_id: request.id } });
    setData(d);
    setQty(Object.fromEntries(Object.entries(d.summaries || {}).map(([v, s]) => [v, String(s.remaining_qty || 0)])));
    setPick({});
  };
  useEffect(() => { if (request) { setKey(uid()); load().catch((e) => toast.error(formatApiError(e))); } }, [request, mk]); // eslint-disable-line

  const rows = useMemo(() => (mat?.items || []).map((it) => ({ vid: it.variant_id || "", label: vLabel(it) || it.code || "—", s: data?.summaries?.[it.variant_id || ""] || {} }))
    .sort((a, b) => sizeKey(a.label) - sizeKey(b.label) || a.label.localeCompare(b.label, "tr")), [mat, data]);
  const suppliers = useMemo(() => {
    const m = {};
    (data?.quotes || []).filter((q) => q.status !== "cancelled").forEach((q) => {
      const s = (m[q.supplier_id] ||= { id: q.supplier_id, name: q.supplier_name, byV: {} });
      if (!s.byV[q.variant_id || ""]) s.byV[q.variant_id || ""] = q; // en güncel teklif (liste yeni→eski)
    });
    return Object.values(m);
  }, [data]);
  const quoteOf = (vid) => suppliers.flatMap((s) => Object.values(s.byV)).find((q) => q.id === pick[vid]);
  const selectAll = (s) => setPick(Object.fromEntries(rows.filter((r) => s.byV[r.vid] && num(r.s.remaining_qty) > 0).map((r) => [r.vid, s.byV[r.vid].id])));
  const sel = rows.filter((r) => pick[r.vid] && num(qty[r.vid]) > 0);
  const poSuppliers = [...new Set(sel.map((r) => quoteOf(r.vid)?.supplier_name))];
  const tot = rows.reduce((a, r) => ({ need: a.need + num(r.s.requirement_qty || 0), linked: a.linked + num(r.s.po_linked_qty || 0), rem: a.rem + num(r.s.remaining_qty || 0) }), { need: 0, linked: 0, rem: 0 });

  const submit = async (allow_over = false) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/purchase-quotes/create-pos", {
        material_id: mat.material_id, kind: mat.kind, variant_id: "", multi_variant: true, allow_over, client_request_id: key,
        selections: sel.map((x) => ({ quote_id: pick[x.vid], quantity: num(qty[x.vid]) })),
      });
      toast.success(`${r.already ? "Zaten oluşturulmuş" : "Oluşturuldu"}: ${r.created.map((p) => `${p.po_no} (${p.supplier_name})`).join(", ")}`);
      setKey(uid()); onSaved?.(); onClose();
    } catch (e) {
      const msg = formatApiError(e);
      if (e?.response?.status === 409 && msg.includes("allow_over") && window.confirm(`${msg}\n\nFazla alımı onaylıyor musunuz?`)) { setBusy(false); return submit(true); }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-5xl bg-slate-900 border-slate-800 p-4" data-testid="bulk-purchase-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-emerald-400" /> Toplu Satın Al — <span className="font-mono text-amber-400">{request?.request_no}</span>
          </DialogTitle>
        </DialogHeader>
        {mats.length > 1 && (
          <select value={mk || `${mats[0].kind}::${mats[0].material_id}`} onChange={(e) => setMk(e.target.value)} className="h-8 rounded-sm bg-slate-950 border border-slate-800 text-sm px-2" data-testid="bulk-purchase-material">
            {mats.map((m) => <option key={`${m.kind}::${m.material_id}`} value={`${m.kind}::${m.material_id}`}>{m.name}</option>)}
          </select>
        )}
        <div className="flex flex-wrap gap-2 text-xs font-mono" data-testid="bulk-purchase-totals">
          <span className="font-sans font-semibold">{mat?.name}</span>
          <span className="border border-slate-800 rounded-sm px-2">Toplam ihtiyaç <b data-testid="bp-total-need">{fmt(tot.need)}</b> {mat?.unit}</span>
          <span className="border border-sky-900 rounded-sm px-2 text-sky-300">Satın almaya bağlanan <b data-testid="bp-total-linked">{fmt(tot.linked)}</b></span>
          <span className="border border-amber-900 rounded-sm px-2 text-amber-300">Kalan <b data-testid="bp-total-remaining">{fmt(tot.rem)}</b></span>
          <span className="text-slate-500">{rows.length} varyant</span>
        </div>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : !suppliers.length ? (
          <div className="text-sm text-slate-400" data-testid="bulk-purchase-no-quotes">Bu ürün için teklif yok. Önce "Toplu Teklif Topla" ile teklif girin.</div>
        ) : (
          <div className="overflow-x-auto border border-slate-800 rounded-sm">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className={`${TH} text-left`}>Beden / No</th><th className={`${TH} text-right`}>İhtiyaç</th><th className={`${TH} text-right`}>Bağlanan</th><th className={`${TH} text-right`}>Kalan</th>
                  {suppliers.map((s) => (
                    <th key={s.id} className={`${TH} text-center normal-case tracking-normal`}>
                      <div className="text-slate-200 text-xs font-sans">{s.name}</div>
                      <div className="text-[10px] text-slate-500">{Object.keys(s.byV).length}/{rows.length} varyant</div>
                      <div className="text-[10px] text-slate-300 font-mono" data-testid={`bp-supplier-total-${s.id}`}>
                        Kalan için: {Object.entries(rows.reduce((a, r) => { const q = s.byV[r.vid]; if (q) a[q.currency] = (a[q.currency] || 0) + num(q.unit_price) * num(r.s.remaining_qty || 0) * (1 + num(q.tax_rate || 0) / 100); return a; }, {})).map(([c, v]) => `${money(v)} ${c}`).join(" + ") || "—"} <span className="text-slate-500">KDV dahil</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => selectAll(s)} className="mt-1 h-6 rounded-sm text-[10px] border-emerald-800 text-emerald-400" data-testid={`bp-select-all-${s.id}`}>
                        <CheckCheck className="w-3 h-3 mr-1" /> Bu tedarikçiden tümünü seç
                      </Button>
                    </th>
                  ))}
                  <th className={`${TH} text-right`}>Sipariş Miktarı</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.vid} data-testid={`bp-row-${r.vid}`}>
                    <td className="px-2 py-1.5 text-xs text-sky-400 font-mono">{r.label}</td>
                    <td className="px-2 py-1.5 text-xs text-right font-mono" data-testid={`bp-need-${r.vid}`}>{fmt(r.s.requirement_qty)}</td>
                    <td className="px-2 py-1.5 text-xs text-right font-mono text-sky-300" data-testid={`bp-linked-${r.vid}`}>{fmt(r.s.po_linked_qty)}</td>
                    <td className="px-2 py-1.5 text-xs text-right font-mono text-amber-300" data-testid={`bp-remaining-${r.vid}`}>{fmt(r.s.remaining_qty)}</td>
                    {suppliers.map((s) => {
                      const q = s.byV[r.vid];
                      const on = q && pick[r.vid] === q.id;
                      return (
                        <td key={s.id} className="px-2 py-1 text-center">
                          {q ? (
                            <button onClick={() => setPick((p) => ({ ...p, [r.vid]: on ? "" : q.id }))} data-testid={`bp-pick-${r.vid}-${s.id}`}
                              className={`w-full rounded-sm border px-2 py-1 text-xs font-mono transition-colors ${on ? "border-emerald-400 bg-emerald-600/40 text-white ring-1 ring-emerald-400" : "border-slate-800 hover:border-slate-600 text-slate-300"}`}>
                              {money(q.unit_price)} {q.currency}{q.tax_rate ? <span className="text-slate-500"> +%{q.tax_rate}</span> : null}
                            </button>
                          ) : <span className="text-slate-600 text-xs">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-right">
                      <Input inputMode="decimal" value={qty[r.vid] ?? ""} onChange={(e) => setQty((x) => ({ ...x, [r.vid]: e.target.value }))}
                        className="bg-slate-950 border-slate-800 rounded-sm h-7 w-20 text-xs text-right font-mono ml-auto" data-testid={`bp-qty-${r.vid}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
          <span data-testid="bp-plan">
            {sel.length ? `${sel.length} varyant · ${fmt(sel.reduce((a, r) => a + num(qty[r.vid]), 0))} ${mat?.unit} → ${poSuppliers.length} PO (${poSuppliers.join(", ")})` : "Tedarikçi seçin (\"Bu tedarikçiden tümünü seç\")"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={onClose} data-testid="bp-cancel">Vazgeç</Button>
            <Button size="sm" className="rounded-sm h-8 bg-emerald-600 hover:bg-emerald-500" disabled={busy || !sel.length} onClick={() => submit(false)} data-testid="bp-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Satın Alma Siparişi Oluştur
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
